/**
 * 入力欄から送る発言の組み立て（ADR-0040）。DOM に依存しない（時計を差し替えてテストする）。
 *
 * 1. 変換中の Enter では送らない（isComposingEnter）。Mac の Safari では、日本語の変換を確定する Enter の keydown で
 *    isComposing が false になることがある。そのときも keyCode は 229 になる。
 * 2. Enter で送った後、少し待つ（merge_wait_ms）。その間に続けて送った・打ち始めた発言は 1 つにまとめて送る
 *    （「次女の」→ 1 秒後に「情報は」が、別々の返事にならないように）。打っている間は待ちが延び、最初の Enter から
 *    merge_max_ms を過ぎたら送る。送信のボタンは待たない（それまでの分とまとめて、すぐ送る）。
 * 待つ秒数は設定（config/client.yaml。GET /v1/me で受け取る）。受け取れなければ DEFAULT_MERGE。
 * 声の端末（apps/voice-client）は使わない（声は 1 回の発話が 1 つの発言）。
 */

export interface MergeSettings {
  /** Enter の後に待つ時間（ミリ秒）。0 なら待たない（まとめない） */
  merge_wait_ms: number;
  /** 打ち続けていても、最初の Enter からこれを過ぎたら送る */
  merge_max_ms: number;
}

/** config/client.yaml の初期値と同じ（未投入・古い Gateway のとき） */
export const DEFAULT_MERGE: MergeSettings = { merge_wait_ms: 1200, merge_max_ms: 8000 };

/** GET /v1/me の input を読む。形が違えば既定値（値は設定の範囲に収める） */
export function mergeSettingsOf(v: unknown): MergeSettings {
  const o = (v ?? {}) as Partial<Record<keyof MergeSettings, unknown>>;
  const num = (x: unknown, d: number, max: number) =>
    typeof x === "number" && Number.isFinite(x) ? Math.min(Math.max(0, Math.floor(x)), max) : d;
  return {
    merge_wait_ms: num(o.merge_wait_ms, DEFAULT_MERGE.merge_wait_ms, 5000),
    merge_max_ms: num(o.merge_max_ms, DEFAULT_MERGE.merge_max_ms, 30_000),
  };
}

/** 変換（IME）の途中の Enter か。true なら送らない（Enter は変換の確定に使われている） */
export function isComposingEnter(e: { isComposing?: boolean; keyCode?: number }): boolean {
  return e.isComposing === true || e.keyCode === 229;
}

export interface Timers {
  now(): number;
  set(fn: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

const browserTimers: Timers = {
  now: () => Date.now(),
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

/**
 * 続けて送った発言をまとめる。add で溜め、待ちが明けたら（または flush で）onSend にまとめた文を 1 回渡す。
 * まとめ方は改行でつなぐ（「次女の\n情報は」）。2 つの発言のつもりだったときも、別々の文として読める
 */
export class UtteranceMerger {
  private parts: string[] = [];
  private firstAt = 0;
  private timer: unknown = null;

  constructor(
    private settings: MergeSettings,
    private readonly onSend: (text: string) => void,
    private readonly timers: Timers = browserTimers,
  ) {}

  /** 設定を受け取り直したとき（GET /v1/me の後） */
  configure(settings: MergeSettings): void {
    this.settings = settings;
  }

  /** まだ送っていない分（まとめた文）。無ければ null */
  get pending(): string | null {
    return this.parts.length > 0 ? this.parts.join("\n") : null;
  }

  /** Enter で送った。待ちが 0 ならすぐ送る */
  add(text: string): void {
    const t = text.trim();
    if (!t) return;
    if (this.parts.length === 0) this.firstAt = this.timers.now();
    this.parts.push(t);
    if (this.settings.merge_wait_ms <= 0) this.flush();
    else this.schedule();
  }

  /** 入力欄が変わった（打っている・変換している）。溜めている間だけ、待ちを延ばす */
  typing(): void {
    if (this.parts.length > 0 && this.settings.merge_wait_ms > 0) this.schedule();
  }

  /** 待たずに送る（送信のボタン・画面を離れるとき） */
  flush(): void {
    if (this.timer !== null) this.timers.clear(this.timer);
    this.timer = null;
    const text = this.pending;
    this.parts = [];
    if (text !== null) this.onSend(text);
  }

  private schedule(): void {
    if (this.timer !== null) this.timers.clear(this.timer);
    const now = this.timers.now();
    const due = Math.min(
      now + this.settings.merge_wait_ms,
      this.firstAt + this.settings.merge_max_ms,
    );
    this.timer = this.timers.set(() => this.flush(), Math.max(0, due - now));
  }
}
